import { EnemyData, EnemyIntent, EnemyStance } from '../types';
import { getCycleForArchetype } from './StanceSystem';

interface EnemyOptions {
  elite?: boolean;
  boss?: boolean;
  isAdd?: boolean;
  stanceCycle?: EnemyStance[];
  phasePatterns?: EnemyIntent[][];
  summonTemplateId?: string;
}

export function createEnemy(
  name: string,
  maxHp: number,
  pattern: EnemyIntent[],
  opts?: EnemyOptions,
): EnemyData {
  return {
    id: `enemy_${name.toLowerCase().replace(/\s/g, '_')}`,
    name,
    maxHp,
    hp: maxHp,
    block: 0,
    pattern,
    patternIndex: 0,
    phasePatterns: opts?.phasePatterns,
    phase: 1,
    stanceCycle: opts?.stanceCycle,
    stanceIndex: 0,
    stance: opts?.stanceCycle?.[0] ?? 'aggro',
    statuses: [],
    mutators: [],
    summonTemplateId: opts?.summonTemplateId,
    isAdd: opts?.isAdd,
    elite: opts?.elite,
    boss: opts?.boss,
  };
}

// --- Summoned Adds ---

export function createSummonedAdd(templateId: string): EnemyData {
  switch (templateId) {
    case 'abacus_mite':
      return createEnemy('Abacus Mite', 14, [
        { type: 'attack', value: 4 },
        { type: 'defend', value: 3 },
      ], { isAdd: true, summonTemplateId: templateId });
    case 'void_shard':
      return createEnemy('Void Shard', 16, [
        { type: 'attack', value: 5 },
        { type: 'attack', value: 3 },
      ], { isAdd: true, summonTemplateId: templateId });
    case 'clock_drone':
      return createEnemy('Clock Drone', 18, [
        { type: 'defend', value: 4 },
        { type: 'attack', value: 6 },
      ], { isAdd: true, summonTemplateId: templateId });
    default:
      return createEnemy('Minion', 12, [
        { type: 'attack', value: 4 },
      ], { isAdd: true, summonTemplateId: templateId });
  }
}

// --- NORMAL ENEMIES ---

export function createSlime(): EnemyData {
  return createEnemy('Slime', 28, [
    { type: 'attack', value: 6 },
    { type: 'attack', value: 8 },
    { type: 'defend', value: 5 },
  ], { stanceCycle: getCycleForArchetype('sentinel') });
}

export function createGremlin(): EnemyData {
  return createEnemy('Gremlin', 18, [
    { type: 'attack', value: 5 },
    { type: 'attack', value: 5 },
    { type: 'attack', value: 9 },
  ], { stanceCycle: getCycleForArchetype('trickster') });
}

export function createFungus(): EnemyData {
  return createEnemy('Fungus', 32, [
    { type: 'attack', value: 4 },
    { type: 'defend', value: 6 },
    { type: 'buff', value: 5, description: 'Heals 5 HP' },
    { type: 'attack', value: 7 },
  ], { stanceCycle: getCycleForArchetype('sentinel') });
}

export function createSkeleton(): EnemyData {
  return createEnemy('Skeleton', 24, [
    { type: 'attack', value: 10 },
    { type: 'defend', value: 3 },
    { type: 'attack', value: 12 },
    { type: 'defend', value: 3 },
  ], { stanceCycle: getCycleForArchetype('bruiser') });
}

// --- ELITE ENEMIES ---

export function createMirrorGolem(): EnemyData {
  return createEnemy('Mirror Golem', 42, [
    { type: 'defend', value: 10 },
    { type: 'attack', value: 0, description: 'Attacks for remaining block' },
    { type: 'summon', value: 0, description: 'Summons a clock drone', summonTemplateId: 'clock_drone' },
    { type: 'attack', value: 7 },
  ], { elite: true, stanceCycle: getCycleForArchetype('sentinel') });
}

export function createNumberLeech(): EnemyData {
  return createEnemy('Number Leech', 26, [
    { type: 'attack', value: 4 },
    { type: 'special', value: 0, description: 'Steals highest card value' },
    { type: 'summon', value: 0, description: 'Summons a void shard', summonTemplateId: 'void_shard' },
    { type: 'attack', value: 8 },
  ], { elite: true, stanceCycle: getCycleForArchetype('trickster') });
}

export function createCrystalGuardian(): EnemyData {
  return createEnemy('Crystal Guard', 48, [
    { type: 'defend', value: 4 },
    { type: 'attack', value: 6 },
    { type: 'special', value: 0, description: 'Gains block = last damage taken' },
    { type: 'attack', value: 10 },
  ], { elite: true, stanceCycle: getCycleForArchetype('sentinel') });
}

// --- ACT 2 NORMAL ENEMIES ---

export function createShade(): EnemyData {
  return createEnemy('Shade', 30, [
    { type: 'defend', value: 8 },
    { type: 'attack', value: 9 },
    { type: 'defend', value: 6 },
    { type: 'attack', value: 12 },
  ], { stanceCycle: getCycleForArchetype('trickster') });
}

export function createBerserker(): EnemyData {
  return createEnemy('Berserker', 22, [
    { type: 'attack', value: 8 },
    { type: 'attack', value: 10 },
    { type: 'attack', value: 14 },
  ], { stanceCycle: getCycleForArchetype('bruiser') });
}

export function createWraith(): EnemyData {
  return createEnemy('Wraith', 35, [
    { type: 'attack', value: 5 },
    { type: 'special', value: 0, description: 'Curses: -2 to next attack' },
    { type: 'attack', value: 8 },
    { type: 'defend', value: 5 },
  ], { stanceCycle: getCycleForArchetype('trickster') });
}

// --- ACT 2 ELITES ---

export function createClockworkKnight(): EnemyData {
  return createEnemy('Clockwork', 50, [
    { type: 'defend', value: 5 },
    { type: 'attack', value: 8 },
    { type: 'summon', value: 0, description: 'Summons a clock drone', summonTemplateId: 'clock_drone' },
    { type: 'attack', value: 14 },
    { type: 'attack', value: 17 },
  ], { elite: true, stanceCycle: getCycleForArchetype('bruiser') });
}

export function createVoidEater(): EnemyData {
  return createEnemy('Void Eater', 40, [
    { type: 'special', value: 0, description: 'Devours: dmg = your cards in hand' },
    { type: 'attack', value: 6 },
    { type: 'defend', value: 8 },
    { type: 'summon', value: 0, description: 'Summons a void shard', summonTemplateId: 'void_shard' },
    { type: 'attack', value: 10 },
  ], { elite: true, stanceCycle: getCycleForArchetype('trickster') });
}

// --- BOSSES ---

export function createAbacus(): EnemyData {
  const phasePatterns: EnemyIntent[][] = [
    [
      { type: 'defend', value: 8 },
      { type: 'special', value: 0, description: 'Counts your defense...' },
      { type: 'attack', value: 10 },
      { type: 'defend', value: 10 },
    ],
    [
      { type: 'summon', value: 0, description: 'Summons an abacus mite', summonTemplateId: 'abacus_mite' },
      { type: 'attack', value: 14 },
      { type: 'buff', value: 6, description: 'Heals 6 HP' },
      { type: 'attack', value: 15 },
    ],
    [
      { type: 'attack', value: 18 },
      { type: 'attack', value: 20 },
      { type: 'buff', value: 5, description: 'Heals 5 HP' },
      { type: 'attack', value: 22 },
    ],
  ];

  return createEnemy('The Abacus', 80, phasePatterns[0], {
    boss: true,
    stanceCycle: ['guard', 'cast', 'aggro', 'guard'],
    phasePatterns,
  });
}

export function createEquation(): EnemyData {
  const phasePatterns: EnemyIntent[][] = [
    [
      { type: 'defend', value: 8 },
      { type: 'special', value: 0, description: 'Reflects: mirrors your attack' },
      { type: 'attack', value: 10 },
      { type: 'defend', value: 12 },
    ],
    [
      { type: 'summon', value: 0, description: 'Summons a void shard', summonTemplateId: 'void_shard' },
      { type: 'special', value: 0, description: 'Casts volatile equation' },
      { type: 'attack', value: 16 },
      { type: 'buff', value: 8, description: 'Heals 8 HP' },
    ],
    [
      { type: 'attack', value: 22 },
      { type: 'attack', value: 24 },
      { type: 'defend', value: 12 },
      { type: 'attack', value: 20 },
    ],
  ];

  return createEnemy('The Equation', 100, phasePatterns[0], {
    boss: true,
    stanceCycle: ['guard', 'cast', 'aggro', 'cast'],
    phasePatterns,
  });
}
